import React, { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../../../../store';
import { UserMessage } from '../../../../../DesignSystem/LiliUserMessage/UserMessage';
import { InvoiceActionsMenu } from '../Modals/InvoiceActionsMenu';
import { InvoiceActionsContext } from '../../contexts/InvoiceActionsContext';
import { ShareLinkAction } from '../Modals/ShareLinkAction';
import { InvoicePage } from '../InvoicePage/InvoicePage';
import { getInvoiceShareLink } from '../../utils';
import { CancelInvoice } from '../Modals/CancelInvoice';
import Spinner, { SpinnerColor } from '../../../../../generic-components/Spinner';
import { MarkAsPaid } from '../Modals/MarkAsPaid';
import { MarkAsUnpaid } from '../Modals/MarkAsUnpaid';
import { ErrorModal } from '../../../../../generic-components/ErrorModal';
import { SocialSharePopUp } from '../../../../../components/SocialSharePopUp';
import { VIEWS_MAP } from '../../../../../router/constants';
import { InvoiceSettingsSubMenu } from '../InvoiceSettings/InvoiceSettingsSubMenu';
import { InvoiceSettingsContext } from '../../contexts/InvoiceSettingsContext';
import { useClearQueryString } from '../../../../../hooks/utilities/useClearQueryString';
import { CloseButton } from '../../../../../DesignSystem/LiliCloseButton/CloseButton';

export const HUB_MODALS = {
  NONE: 'NONE',
  INVOICE_PAGE: 'INVOICE_PAGE',
  ACTIONS_MENU: 'ACTIONS_MENU',
  SHARE_LINK: 'SHARE_LINK',
  CANCEL_INVOICE: 'INVOICE_CANCEL',
  SPINNER: 'SPINNER',
  MARK_AS_PAID: 'MARK_AS_PAID',
  MARK_AS_UNPAID: 'MARK_AS_UNPAID',
  ERROR: 'ERROR',
  SOCIAL_SHARE: 'SOCIAL_SHARE',
  INVOICE_SETTINGS: 'INVOICE_SETTINGS',
};

export const HUB_MODALS_STYLES = {
  [HUB_MODALS.INVOICE_PAGE]: {
    main: 'bg-bw07 flex fixed inset-0 bg-opacity-70 z-50 justify-end items-stretch h-full',
    content: 'cursor-default block w-[646px] static h-full',
    exit: 'absolute top-4 left-4 cursor-pointer z-10',
  },
  [HUB_MODALS.ACTIONS_MENU]: {
    main: 'flex fixed inset-0 w-screen bg-opacity-70 z-50 justify-end',
    content: 'cursor-default block items-start',
  },
  [HUB_MODALS.SHARE_LINK]: {
    main: 'bg-bw07 flex fixed inset-0 h-screen w-screen bg-opacity-70 z-50 items-center justify-center',
    content: 'cursor-default block items-start',
    exit: 'absolute top-5 right-5 cursor-pointer z-10',
  },
  [HUB_MODALS.CANCEL_INVOICE]: {
    main: 'bg-bw07 flex fixed inset-0 h-screen w-screen bg-opacity-70 z-50 items-center justify-center',
    content: 'cursor-default block items-start',
    exit: 'absolute top-5 right-5 cursor-pointer z-10',
  },
  [HUB_MODALS.SPINNER]: {
    main: 'bg-[#000]/[.5] flex fixed inset-0 h-screen w-screen items-center justify-center z-50',
  },
  [HUB_MODALS.MARK_AS_PAID]: {
    main: 'bg-bw07 flex fixed inset-0 h-screen w-screen bg-opacity-70 z-50 items-center justify-center',
    content: 'cursor-default block items-start',
    exit: 'absolute top-3 right-3 cursor-pointer z-10',
  },
  [HUB_MODALS.MARK_AS_UNPAID]: {
    main: 'bg-bw07 flex fixed inset-0 h-screen w-screen bg-opacity-70 z-50 items-center justify-center',
    content: 'cursor-default block items-start',
    exit: 'absolute top-3 left-3 cursor-pointer z-10',
  },
  [HUB_MODALS.SOCIAL_SHARE]: {
    main: 'bg-bw07 flex fixed inset-0 h-screen w-screen bg-opacity-70 z-50 items-center justify-center',
    content: 'cursor-default block items-start',
  },
  [HUB_MODALS.INVOICE_SETTINGS]: {
    main: 'flex fixed inset-0 w-screen z-50',
    content: 'cursor-default block items-start',
  },
};

export const InvoiceHubModals = () => {
  const activeModal = useStore((state) => state.activeModal);
  const setActiveModal = useStore((state) => state.setActiveModal);
  const selectedInvoice = useStore((state) => state.selectedInvoice);
  const setSelectedInvoice = useStore((state) => state.setSelectedInvoice);
  const error = useStore((state) => state.error);
  const resetError = useStore((state) => state.resetError);
  const { invoiceActionsReferenceElement } = useContext(InvoiceActionsContext);
  const { invoiceSettingsReferenceElement } = useContext(InvoiceSettingsContext);
  const activeMainWrapperStyles = HUB_MODALS_STYLES[activeModal]?.main;
  const activeContentWrapperStyles = HUB_MODALS_STYLES[activeModal]?.content;
  const activeExitIconStyles = HUB_MODALS_STYLES[activeModal]?.exit;
  const navigate = useNavigate();
  const { clearQueryString } = useClearQueryString();

  useEffect(() => setActiveModal(HUB_MODALS.NONE), []);
  const onClose = () => {
    setActiveModal(HUB_MODALS.NONE);
    setSelectedInvoice('0');
    if (error) resetError();
    clearQueryString();
  };
  if (activeModal === HUB_MODALS.NONE) return null;
  if (activeModal === HUB_MODALS.ERROR && error) {
    return <ErrorModal onClose={onClose} text={error?.localizedMessage} onButtonClick={onClose} />;
  }

  return (
    <UserMessage
      id='invoices-modal'
      mainWrapperOverrideClassName={`${activeMainWrapperStyles}`} // This fixes wrong styling passing for UserMessage, but it's not the best solution
      contentWrapperOverrideClassName={activeContentWrapperStyles}
      onClick={onClose}
    >
      {!!activeExitIconStyles && (
        <CloseButton id='closeButton' className={`${activeExitIconStyles}`} onClick={onClose} />
      )}
      {activeModal === HUB_MODALS.INVOICE_PAGE && <InvoicePage selectedInvoice={selectedInvoice} />}
      {activeModal === HUB_MODALS.ACTIONS_MENU && (
        <InvoiceActionsMenu referenceElement={invoiceActionsReferenceElement} onClose={onClose} />
      )}
      {activeModal === HUB_MODALS.SHARE_LINK && (
        <ShareLinkAction linkToShare={getInvoiceShareLink(selectedInvoice?.paymentUrl)} onClose={onClose} />
      )}
      {activeModal === HUB_MODALS.CANCEL_INVOICE && (
        <CancelInvoice
          externalId={selectedInvoice?.externalId}
          invoiceNumber={selectedInvoice?.invoiceNumber}
          invoiceStatus={selectedInvoice?.status}
          onClose={onClose}
        />
      )}
      {activeModal === HUB_MODALS.SPINNER && <Spinner spinnerColor={SpinnerColor.Blue} height='60px' width='60px' />}
      {activeModal === HUB_MODALS.MARK_AS_PAID && (
        <MarkAsPaid
          onClose={onClose}
          invoiceAmount={selectedInvoice?.amount}
          externalId={selectedInvoice?.externalId}
          invoiceStatus={selectedInvoice?.status}
        />
      )}
      {activeModal === HUB_MODALS.MARK_AS_UNPAID && (
        <MarkAsUnpaid
          onClose={onClose}
          externalId={selectedInvoice?.externalId}
          invoiceStatus={selectedInvoice?.status}
        />
      )}
      {activeModal === HUB_MODALS.SOCIAL_SHARE && (
        <SocialSharePopUp
          onClose={() => navigate(`/${VIEWS_MAP.INVOICES}`)}
          recipientName={selectedInvoice?.recipient}
          invoiceNumber={selectedInvoice?.invoiceNumber}
          paymentUrl={selectedInvoice?.paymentUrl}
        />
      )}
      {activeModal === HUB_MODALS.INVOICE_SETTINGS && (
        <InvoiceSettingsSubMenu onClose={onClose} referenceElement={invoiceSettingsReferenceElement} />
      )}
    </UserMessage>
  );
};
